/**
 * 统一数据表格组件
 */
import React, { memo, useMemo } from 'react';
import { Table, Card, Space, Button, Typography, Dropdown, Tooltip } from 'antd';
import { ReloadOutlined, MoreOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * 数据表格组件
 * @param {Object} props
 * @param {Array} props.columns - 表格列配置
 * @param {Array} props.dataSource - 数据源
 * @param {Object} props.pagination - 分页配置
 * @param {Function} props.onChange - 分页/排序/筛选变化回调
 * @param {boolean} props.loading - 加载状态
 * @param {string} props.title - 卡片标题
 * @param {React.ReactNode} props.extra - 卡片右上角额外内容
 * @param {Array} props.actions - 行操作按钮
 * @param {Function} props.onReload - 刷新回调
 * @param {boolean} props.showReload - 是否显示刷新按钮
 * @param {string} props.className - 自定义类名
 */
const DataTable = ({
  columns = [],
  dataSource = [],
  pagination = { current: 1, pageSize: 20, total: 0 },
  onChange,
  loading = false,
  title,
  extra,
  actions = [],
  onReload,
  showReload = true,
  showSearch = true,
  searchPlaceholder = '搜索...',
  onSearch,
  rowKey = 'id',
  rowSelection,
  className = '',
  ...restProps
}) => {
  // 默认列操作渲染
  const defaultColumns = useMemo(() => {
    if (actions.length === 0) return columns;

    const actionColumn = {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {actions.map((action, index) => {
            if (action.dropdown) {
              return (
                <Dropdown
                  key={index}
                  menu={{ items: action.dropdownItems?.(record) }}
                  trigger={['click']}
                >
                  <Button type="text" size="small" icon={<MoreOutlined />} />
                </Dropdown>
              );
            }

            return (
              <Tooltip key={index} title={action.tooltip}>
                <Button
                  type={action.type || 'text'}
                  size="small"
                  icon={action.icon}
                  onClick={() => action.onClick?.(record)}
                  danger={action.danger}
                  disabled={action.disabled?.(record)}
                >
                  {action.label}
                </Button>
              </Tooltip>
            );
          })}
        </Space>
      )
    };

    return [...columns, actionColumn];
  }, [columns, actions]);

  const handleTableChange = (paginationConfig, filters, sorter) => {
    onChange?.({
      pagination: paginationConfig,
      filters,
      sorter
    });
  };

  const mergedPagination = useMemo(() => ({
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => (
      <Text type="secondary">
        第 {range[0]}-{range[1]} 条，共 {total} 条
      </Text>
    ),
    ...pagination
  }), [pagination]);

  return (
    <Card
      className={`data-table-card ${className}`}
      title={title}
      extra={
        <Space>
          {extra}
          {showReload && onReload && (
            <Tooltip title="刷新数据">
              <Button
                type="text"
                icon={<ReloadOutlined spin={loading} />}
                onClick={onReload}
              />
            </Tooltip>
          )}
        </Space>
      }
    >
      <Table
        columns={defaultColumns}
        dataSource={dataSource}
        pagination={mergedPagination}
        onChange={handleTableChange}
        loading={loading}
        rowKey={rowKey}
        rowSelection={rowSelection}
        scroll={{ x: 'max-content' }}
        size="middle"
        {...restProps}
      />
    </Card>
  );
};

export default memo(DataTable);